import React, { useState } from "react";
import axios from "axios";
import { ArrowLeft, CheckCircle2, MessageCircle, Sparkles, AlertTriangle, BellRing } from "lucide-react";
import { toast } from "sonner";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { buildWhatsAppLink, STRIPE_CONSULTATION_URL } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  in_poland: "",
  situation: "",
  stay_end: "",
  has_documents: "",
  city: "",
  name: "",
  email: "",
  phone: "",
  reminder_optin: false,
};

export default function LeadForm() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const update = (field, value) =>
    setForm((f) => ({ ...f, [field]: value }));

  const validate = () => {
    if (!form.in_poland) return "يرجى تحديد إن كنت في بولندا حالياً";
    if (!form.situation) return "يرجى اختيار وضعك (دراسة / عمل / أخرى)";
    if (!form.has_documents) return "يرجى تحديد حالة مستنداتك";
    if (!form.name.trim()) return "يرجى كتابة اسمك";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email))
      return "يرجى كتابة بريد إلكتروني صحيح";
    if (!form.phone.trim()) return "يرجى كتابة رقم الهاتف / واتساب";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/leads`, form);
      setResult(data);
      toast.success("تم استلام طلبك بنجاح");
      setTimeout(() => {
        const el = document.querySelector("#lead-result");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (e2) {
      console.error(e2);
      toast.error("حدث خطأ أثناء الإرسال. حاول مرة أخرى.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="lead-form"
      className="py-20 lg:py-24 bg-gradient-to-br from-slate-50 via-white to-emerald-50/40"
      data-testid="lead-form-section"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-start max-w-2xl mb-10">
          <div className="text-emerald-700 text-sm font-semibold mb-3">
            التحقق المجاني من مسار الإقامة
          </div>
          <h2 className="font-tajawal text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
            لست متأكداً من أين تبدأ؟
          </h2>
          <p className="mt-3 text-base md:text-lg text-slate-600">
            أجب عن بعض الأسئلة القصيرة وسنساعدك على تحديد: المسار الأنسب
            لحالتك، وأهم خطر مبكر، وهل الاستشارة تكفي أم الدعم الكامل أفضل.
          </p>
        </div>

        {!result ? (
          <form
            onSubmit={onSubmit}
            className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 lg:p-10 shadow-sm"
            data-testid="lead-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="هل أنت في بولندا حالياً؟">
                <Select
                  value={form.in_poland}
                  onValueChange={(v) => update("in_poland", v)}
                >
                  <SelectTrigger
                    className="bg-slate-50 border-slate-200 rounded-xl h-12"
                    data-testid="field-in_poland"
                  >
                    <SelectValue placeholder="اختر" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">نعم، أنا في بولندا</SelectItem>
                    <SelectItem value="no">لا، أخطط للانتقال</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <Field label="ما هو وضعك؟">
                <Select
                  value={form.situation}
                  onValueChange={(v) => update("situation", v)}
                >
                  <SelectTrigger
                    className="bg-slate-50 border-slate-200 rounded-xl h-12"
                    data-testid="field-situation"
                  >
                    <SelectValue placeholder="اختر" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student">دراسة</SelectItem>
                    <SelectItem value="work">عمل</SelectItem>
                    <SelectItem value="other">حالة أخرى</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <Field label="متى تنتهي إقامتك الحالية؟">
                <input
                  type="text"
                  placeholder="مثال: 2026-03-15 أو لا أعرف"
                  value={form.stay_end}
                  onChange={(e) => update("stay_end", e.target.value)}
                  className="bg-slate-50 border border-slate-200 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 rounded-xl h-12 px-4 w-full transition-colors outline-none"
                  data-testid="field-stay_end"
                />
              </Field>

              <Field label="هل مستنداتك الأساسية جاهزة؟">
                <Select
                  value={form.has_documents}
                  onValueChange={(v) => update("has_documents", v)}
                >
                  <SelectTrigger
                    className="bg-slate-50 border-slate-200 rounded-xl h-12"
                    data-testid="field-has_documents"
                  >
                    <SelectValue placeholder="اختر" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">نعم، جاهزة</SelectItem>
                    <SelectItem value="partial">بعضها جاهز</SelectItem>
                    <SelectItem value="no">لا، لم أبدأ بعد</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <Field label="في أيّ مدينة ستُعالج حالتك؟">
                <input
                  type="text"
                  placeholder="مثال: وارسو، كراكوف..."
                  value={form.city}
                  onChange={(e) => update("city", e.target.value)}
                  className="bg-slate-50 border border-slate-200 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 rounded-xl h-12 px-4 w-full transition-colors outline-none"
                  data-testid="field-city"
                />
              </Field>

              <Field label="الاسم">
                <input
                  type="text"
                  placeholder="اسمك الكامل"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="bg-slate-50 border border-slate-200 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 rounded-xl h-12 px-4 w-full transition-colors outline-none"
                  data-testid="field-name"
                  required
                />
              </Field>

              <Field label="البريد الإلكتروني">
                <input
                  type="email"
                  placeholder="name@example.com"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="bg-slate-50 border border-slate-200 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 rounded-xl h-12 px-4 w-full transition-colors outline-none"
                  data-testid="field-email"
                  required
                />
              </Field>

              <Field label="الهاتف / واتساب">
                <input
                  type="tel"
                  placeholder="+48 ..."
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="bg-slate-50 border border-slate-200 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 rounded-xl h-12 px-4 w-full transition-colors outline-none"
                  data-testid="field-phone"
                  required
                  dir="ltr"
                />
              </Field>
            </div>

            <label
              htmlFor="reminder-optin"
              className="mt-7 flex items-start gap-3 p-4 bg-emerald-50/60 border border-emerald-200 rounded-xl cursor-pointer hover:bg-emerald-50 transition-colors"
              data-testid="reminder-optin-wrapper"
            >
              <Checkbox
                id="reminder-optin"
                checked={form.reminder_optin}
                onCheckedChange={(v) => update("reminder_optin", v === true)}
                className="mt-0.5 h-5 w-5 border-emerald-700 data-[state=checked]:bg-emerald-700 data-[state=checked]:border-emerald-700"
                data-testid="field-reminder_optin"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <BellRing className="w-4 h-4 text-emerald-700" />
                  <span className="font-tajawal font-bold text-sm text-slate-900">
                    تنبيه مجاني قبل انتهاء إقامتك
                  </span>
                  <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-100 border border-emerald-200 rounded-full px-2 py-0.5">
                    مجاناً
                  </span>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">
                  نرسل لك تذكيراً قبل انتهاء إقامتك بـ 30 و 14 و 7 أيام حتى لا
                  تفوّت موعد التجديد. يمكنك إلغاء الاشتراك في أيّ وقت.
                </p>
              </div>
            </label>

            <div className="mt-7 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <p className="text-xs text-slate-500 leading-relaxed max-w-md">
                بإرسالك النموذج، توافق على التواصل معك عبر البريد الإلكتروني
                أو واتساب لتوضيح وضعك. لا نشارك بياناتك مع أيّ جهة أخرى.
              </p>
              <button
                type="submit"
                disabled={loading}
                className="btn-accent rounded-xl px-6 py-3.5 font-semibold inline-flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                data-testid="lead-form-submit"
              >
                {loading ? "جارٍ الإرسال..." : "تحقق من حالتي"}
                {!loading && <ArrowLeft className="w-4 h-4" />}
              </button>
            </div>
          </form>
        ) : (
          <ResultCard result={result} onReset={() => setResult(null)} />
        )}
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="block text-sm font-medium text-slate-700 mb-2">
        {label}
      </Label>
      {children}
    </div>
  );
}

function ResultCard({ result, onReset }) {
  const rec = result.recommendation;
  const waHref = buildWhatsAppLink(result.whatsapp_message);
  const recommendsConsultation = /استشارة/.test(rec.next_step);

  return (
    <div
      id="lead-result"
      className="bg-white border-2 border-emerald-200 rounded-2xl p-7 sm:p-9 lg:p-10 shadow-sm"
      data-testid="lead-result"
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
          <CheckCircle2 className="w-6 h-6" />
        </div>
        <div>
          <div className="text-xs text-emerald-700 font-semibold mb-0.5">
            تم استلام طلبك
          </div>
          <h3 className="font-tajawal text-2xl font-bold text-slate-900">
            إليك التوصية المبدئية لحالتك
          </h3>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <InfoTile
          icon={<Sparkles className="w-5 h-5" />}
          label="المسار الأنسب"
          value={rec.path}
          tone="gold"
          testid="result-path"
        />
        <InfoTile
          icon={<AlertTriangle className="w-5 h-5" />}
          label="أهم خطر"
          value={rec.risk}
          tone="warn"
          testid="result-risk"
        />
        <InfoTile
          icon={<ArrowLeft className="w-5 h-5" />}
          label="الخطوة التالية الموصى بها"
          value={rec.next_step}
          tone="slate"
          testid="result-next-step"
        />
      </div>

      {optedInReminder && (
        <div
          className="mt-6 flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl"
          data-testid="result-reminder-confirmation"
        >
          <BellRing className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-slate-700 leading-relaxed">
            <span className="font-semibold text-emerald-800">تم تفعيل التنبيه المجاني.</span>{" "}
            سنذكّرك قبل انتهاء إقامتك بـ 30 و 14 و 7 أيام على بريدك الإلكتروني.
          </div>
        </div>
      )}

      <div className="mt-8 border-t border-slate-100 pt-6 flex flex-wrap gap-3">
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1DA851] text-white rounded-xl px-6 py-3.5 font-semibold transition-colors"
          data-testid="result-whatsapp"
        >
          <MessageCircle className="w-4 h-4" />
          تابع عبر واتساب مع ملخص حالتك
        </a>
        {recommendsConsultation && (
          <a
            href={STRIPE_CONSULTATION_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-slate rounded-xl px-6 py-3.5 font-semibold inline-flex items-center gap-2"
            data-testid="result-book-consultation"
          >
            احجز الاستشارة · 100€
            <ArrowLeft className="w-4 h-4" />
          </a>
        )}
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-2 bg-transparent border-2 border-slate-200 hover:border-slate-300 text-slate-700 rounded-xl px-6 py-3.5 font-semibold transition-colors"
          data-testid="result-reset"
        >
          تعديل الإجابات
        </button>
      </div>
    </div>
  );
}

function InfoTile({ icon, label, value, tone, testid }) {
  const tones = {
    gold: "bg-emerald-50 border-emerald-200 text-emerald-800",
    warn: "bg-rose-50 border-rose-200 text-rose-800",
    slate: "bg-slate-900 border-slate-900 text-white",
  };
  return (
    <div
      className={"rounded-xl border p-5 " + tones[tone]}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider mb-2 opacity-80">
        {icon}
        <span>{label}</span>
      </div>
      <div
        className={
          "text-sm leading-relaxed font-medium " +
          (tone === "slate" ? "text-white" : "text-slate-800")
        }
      >
        {value}
      </div>
    </div>
  );
}
